import React, { useState } from 'react';
import styled from 'styled-components';

import { EmailSyncStatus, EmailAliasStatus } from '~/graphql/types';
import { SynchroniseEntityType } from '~/scenes/External/Nylas/util';

import EmailAlias from './EmailAlias';
import { InputGroup } from '~/components/Inputs';
import { DeleteInputButton, LinkButton } from '~/components/Buttons';
import {
  isSuccessEmailSyncStatus,
  isInitialisingEmailSyncStatus,
} from '~/util/constants';
import TEST_ID from './SynchronisedMailbox.testid';
import useConfirmModal from '~/components/Alerts/useConfirmModal';
import LinkedSubField from '~/components/Inputs/LinkedSubField';
import HelpButton from '~/components/Buttons/HelpButton';
import { INPUT_BUTTON_TYPE } from '~/components/Inputs/Input.type';
import Icon from '~/components/Icon';
import { InputWithTooltip } from '~/components/Inputs/Input';

const accountWarning =
  'Door het verwijderen van het e-mailaccount kunnen flows geen e-mail meer versturen. Ook kunnen gekoppelde e-mails niet meer worden bekeken.';
const userAndOfficeWarning =
  'Door het verwijderen kunnen gekoppelde e-mails niet meer worden bekeken.';
const text = {
  inputLabel: 'E-mail',
  synchronizeButtonText: 'synchroniseer nieuw e-mail account',
  addAliasText: 'Alias toevoegen',
  header: 'E-mailintegratie',
  deleteConfirmModalLabels: (entityType: SynchroniseEntityType) => ({
    title: 'Waarschuwing',
    message: `${
      entityType === 'Account' ? accountWarning : userAndOfficeWarning
    }`,
    buttonConfirmTitle: 'Verwijderen',
    buttonCancelTitle: 'Annuleren',
  }),
};

type Props = {
  email: string;
  syncState: EmailSyncStatus;
  onDelete: () => void;
  onResyncRequested: () => void;
  mayEdit: boolean;
  loading: boolean;

  /** Where this component is from */
  entityType: SynchroniseEntityType;
  entityId: string;
  emailAliasState: EmailAliasStatus | null;
  emailAliasValidatedDate: Date | null;
  initialEmailAlias: string | null;
  onSuccessfulAliasVerificationStart: () => void;
  onSuccessfulAliasDelete: () => void;
};
const SynchronisedMailbox: React.FC<Props> = ({
  email,
  syncState,
  onDelete,
  onResyncRequested,
  loading,
  entityType,
  entityId,
  mayEdit,
  initialEmailAlias,
  emailAliasState,
  emailAliasValidatedDate,
  onSuccessfulAliasVerificationStart,
  onSuccessfulAliasDelete,
}) => {
  const [triggerConfirmModal] = useConfirmModal(
    text.deleteConfirmModalLabels(entityType),
    onDelete,
    undefined,
    TEST_ID.DELETE_CONFIRM_MODAL,
  );

  const syncInfoButtonProps = getSyncInfoButtonProps({
    syncState,
    onResyncRequested,
  });

  return (
    <>
      <InputGroup>
        <InputGroupContainer>
          <StyledInputGroup>
            <InputWithTooltip
              label={text.inputLabel}
              data-testid={TEST_ID.GET_MAILBOX_INPUT(email)}
              disabled={true}
              value={email}
              name={`admin-input-${email}`}
              accent={syncInfoButtonProps.accent}
              button={syncInfoButtonProps}
            />
            <DeleteInputButton
              dataTestId={TEST_ID.GET_MAILBOX_DELETE_BUTTON(email)}
              onClick={triggerConfirmModal}
              disabled={!(mayEdit === true)}
              loading={loading}
            />
          </StyledInputGroup>
          <AliasComponent
            syncState={syncState}
            mayEdit={mayEdit}
            email={email}
            entityId={entityId}
            entityType={entityType}
            emailAliasState={emailAliasState}
            emailAliasValidatedDate={emailAliasValidatedDate}
            onSuccessfulAliasVerificationStart={
              onSuccessfulAliasVerificationStart
            }
            onSuccessfulAliasDelete={onSuccessfulAliasDelete}
            initialEmailAlias={initialEmailAlias}
          />
        </InputGroupContainer>
      </InputGroup>
    </>
  );
};

type AliasComponentProps = {
  email: string;
  syncState: EmailSyncStatus;
  mayEdit: boolean;
  entityId: string;
  entityType: SynchroniseEntityType;
  emailAliasState: EmailAliasStatus | null;
  emailAliasValidatedDate: Date | null;
  initialEmailAlias: string | null;
  onSuccessfulAliasDelete: () => void;
  onSuccessfulAliasVerificationStart: () => void;
};
const AliasComponent: React.FC<AliasComponentProps> = ({
  syncState,
  mayEdit,
  email,
  entityId,
  entityType,
  emailAliasState,
  initialEmailAlias,
  emailAliasValidatedDate,
  onSuccessfulAliasDelete,
  onSuccessfulAliasVerificationStart,
}) => {
  const [emailAlias, setEmailAlias] = useState<string | null>(
    initialEmailAlias,
  );
  const [isAddingOrExists, setAddingOrExists] = useState(
    initialEmailAlias != null && emailAliasState != null,
  );

  if (!isAddingOrExists)
    return (
      <LinkButtonContainer>
        <LinkButton
          disabled={!mayEdit || !isSuccessEmailSyncStatus(syncState)}
          onClick={() => setAddingOrExists(true)}
          testId={TEST_ID.ADD_ALIAS_BUTTON}
        >
          {text.addAliasText}
        </LinkButton>
        <StyledHelpButton
          link={{
            link: 'https://help.dathuis.nl/nl/articles/3966599-hoe-voeg-ik-een-alias-aan-mijn-e-mailadres-toe',
          }}
        />
      </LinkButtonContainer>
    );

  return (
    <LinkedSubField>
      {setRef => (
        <EmailAlias
          parentEmail={email}
          emailAlias={{
            email: emailAlias,
            state: emailAliasState,
            validatedDate: emailAliasValidatedDate,
          }}
          setRef={setRef}
          onChange={newAlias => {
            setEmailAlias(newAlias);
          }}
          onSuccessfulVerificationStart={({ emailAlias }) => {
            setEmailAlias(emailAlias);

            onSuccessfulAliasVerificationStart();
          }}
          onSuccessfulDelete={() => {
            setEmailAlias(null);
            setAddingOrExists(false);

            onSuccessfulAliasDelete();
          }}
          entityId={entityId}
          entityType={entityType}
          mayEdit={mayEdit}
        />
      )}
    </LinkedSubField>
  );
};

const getSyncInfoButtonProps = ({
  syncState,
  onResyncRequested,
}: {
  syncState: EmailSyncStatus;
  onResyncRequested: () => void;
}) => {
  const onClick = !isSuccessEmailSyncStatus(syncState)
    ? onResyncRequested
    : undefined;
  if (isSuccessEmailSyncStatus(syncState)) {
    return {
      icon: <Icon name="check" strokeWidth={2.5} />,
      type: INPUT_BUTTON_TYPE.SUCCESS,
      tooltipText: null,
      accent: false,
      onClick,
    };
  }
  if (isInitialisingEmailSyncStatus(syncState)) {
    return {
      icon: <Icon name="spinner" spin />,
      type: INPUT_BUTTON_TYPE.PENDING,
      tooltipText:
        'Het e-mailaccount wordt voor de eerste keer gesynchroniseerd',
      accent: false,
      onClick,
    };
  }

  return {
    icon: <Icon name="reload" />,
    type: INPUT_BUTTON_TYPE.ACCENT,
    tooltipText:
      'De synchronisatie is verbroken. Klik hier om opnieuw te synchroniseren.',
    accent: true,
    onClick,
  };
};
const LinkButtonContainer = styled.div<{}>`
  ${({ theme }) => `
    margin-top: ${theme.space('l')};
  `}
`;

const StyledInputGroup = styled(InputGroup)<{}>`
  margin-top: 0;
  margin-bottom: 0;
`;

const InputGroupContainer = styled.div<{}>`
  width: 100%;
`;

const StyledHelpButton = styled(HelpButton)<{}>`
  ${({ theme }) => `
    margin-left: ${theme.space('s')};
  `}
`;

export default SynchronisedMailbox;
